import { useMemo, useState } from 'react';
import { complexityTotal } from '@/components/ui/Complexity';
import { SectionHeader } from '@/components/ui/Primitives';
import { MinorProjectRow, ProjectCard } from '@/components/ui/ProjectCard';
import { Reveal } from '@/components/ui/Reveal';
import { cx } from '@/lib/format';
import type { Bootstrap, Project } from '@/lib/types';
import { useI18n } from '@/store/preferences';

export function ProjectsPage({ data }: { data: Bootstrap }) {
  const { t } = useI18n();
  const [filter, setFilter] = useState<string | null>(null);

  const major = useMemo(() => {
    const load = (project: Project) =>
      project.complexity ? complexityTotal(project.complexity) : 0;
    return data.projects.filter((p) => p.scale === 'MAJOR').sort((a, b) => load(b) - load(a));
  }, [data.projects]);

  const minor = useMemo(() => data.projects.filter((p) => p.scale === 'MINOR'), [data.projects]);

  // Only technologies that appear in a major project become chips, so the
  // filter bar can never offer an empty result.
  const filters = useMemo(() => {
    const counts = new Map<string, { slug: string; name: string; count: number }>();
    for (const project of major) {
      for (const tech of project.technologies) {
        const existing = counts.get(tech.slug);
        if (existing) existing.count += 1;
        else counts.set(tech.slug, { slug: tech.slug, name: tech.name, count: 1 });
      }
    }
    const all = [...counts.values()].sort(
      (a, b) => b.count - a.count || a.name.localeCompare(b.name)
    );
    // Only technologies shared by more than one project: a chip that narrows
    // six cards down to one is a link, not a filter.
    const shared = all.filter((entry) => entry.count > 1);
    return shared.length >= 4 ? shared : all;
  }, [major]);

  const visible = useMemo(
    () => (filter ? major.filter((p) => p.technologies.some((tech) => tech.slug === filter)) : major),
    [major, filter]
  );

  return (
    <div className="pb-24 pt-28 sm:pb-32 sm:pt-32">
      <div className="shell">
        <SectionHeader
          eyebrow={t.sections.selectedWork}
          title={t.sections.allProjects}
          intro={t.index.caption}
        />

        <div className="mb-8 flex flex-wrap gap-1.5" role="group" aria-label={t.project.filterBy}>
          <FilterChip active={filter === null} onClick={() => setFilter(null)}>
            {t.project.filterAll}
            <span className="tabular text-faint">{major.length}</span>
          </FilterChip>

          {filters.map((entry) => (
            <FilterChip
              key={entry.slug}
              active={filter === entry.slug}
              onClick={() => setFilter(filter === entry.slug ? null : entry.slug)}
            >
              {entry.name}
              <span className="tabular text-faint">{entry.count}</span>
            </FilterChip>
          ))}
        </div>

        {visible.length === 0 ? (
          <p className="py-20 text-center text-muted">{t.project.noResults}</p>
        ) : (
          <ul className="grid gap-5 lg:grid-cols-2">
            {visible.map((project, index) => (
              <li
                key={project.id}
                className="stagger-item stagger-in-self h-full"
                style={{ '--i': index } as React.CSSProperties}
              >
                <ProjectCard project={project} />
              </li>
            ))}
          </ul>
        )}

        {minor.length > 0 && !filter && (
          <section className="mt-24">
            <SectionHeader
              eyebrow={t.sections.minorWork}
              title={t.sections.minorWork}
              intro={t.sections.minorWorkIntro}
            />
            <Reveal>
              <div className="plate">
                {minor.map((project) => (
                  <MinorProjectRow key={project.id} project={project} />
                ))}
              </div>
            </Reveal>
          </section>
        )}
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        'inline-flex items-center gap-1.5 rounded-plate border px-2.5 py-1.5',
        'font-mono text-2xs uppercase tracking-[0.12em] transition-colors duration-200',
        active
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-line bg-transparent text-muted hover:border-ink/30 hover:text-ink'
      )}
    >
      {children}
    </button>
  );
}
